/**
 * WorkflowVersions — entry component for the Workflow Versions tab
 * (mounted under `?tab=versions`).
 *
 * Renders the page chrome (header + timeline rail) and orchestrates
 * the four lifecycle states:
 *   - no thread        → "no analysis yet" notice
 *   - initial loading  → shaped skeleton inside the surface
 *   - fetch error      → inline error block with Retry
 *   - empty list       → "no versions yet" notice
 *   - data             → rail of VersionCards + load-more button
 *
 * Restore flow lives entirely inside this component: the rail's
 * Restore buttons call openRestoreConfirm, the dialog is mounted at
 * the page level, and confirm fires a success/error toast keyed off
 * the live `agents_affected` from the restore response.
 */
import React from 'react';
import { useWorkflowVersions } from './hooks/useWorkflowVersions';
import VersionCard from './components/VersionCard/VersionCard';
import VersionsSkeleton from './components/Skeleton/VersionsSkeleton';
import RestoreConfirmDialog from './components/RestoreConfirmDialog/RestoreConfirmDialog';
import { groupVersionsByDate } from './utils/groupByDate';
import { notify } from '../../services/notificationBus';
import { Button } from '../../components/ui/Button2';
import { AnalysisEmpty as AnalysisEmptyJsx } from '../../components/ui/Empty';
import styles from './WorkflowVersions.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX.
const ButtonAny = Button as unknown as React.ComponentType<any>;
// AnalysisEmpty is a JSX component; cast for use from TSX.
const AnalysisEmpty = AnalysisEmptyJsx as unknown as React.ComponentType<any>;

export interface WorkflowVersionsProps {
  threadId?: string | null;
  sessionId?: string | null;
  workflowStatus?: string | null;
  onAfterRestore?: () => void;
  /** True while the workspace's getSession fetch is in flight —
   *  threadId isn't knowable yet, so render the skeleton, not the
   *  "no analysis" notice. */
  projectLoading?: boolean;
}

export default function WorkflowVersions({
  threadId,
  onAfterRestore,
  projectLoading = false,
}: WorkflowVersionsProps) {
  const { state, actions } = useWorkflowVersions({
    threadId,
    onAfterRestore,
  });

  if (projectLoading) {
    return (
      <div className={styles.shell}>
        <header className={styles.header}>
          <h1 className={styles.title}>Workflow Versions</h1>
        </header>
        <div className={styles.surface}>
          <div className={styles.rail}>
            <VersionsSkeleton rows={4} />
          </div>
        </div>
      </div>
    );
  }

  if (!threadId) {
    return <AnalysisEmpty />;
  }

  const hasMore =
    state.versions.length > 0 &&
    state.versions.length < state.pagination.total;

  const restoreTarget =
    state.pendingRestore != null
      ? state.byNumber[state.pendingRestore] ?? null
      : null;

  const handleConfirmRestore = async () => {
    const target = state.pendingRestore;
    const response = await actions.confirmRestore();
    if (response) {
      const agents = response.agents_affected ?? [];
      const detail =
        agents.length > 0
          ? ` · ${agents.length} agent${agents.length === 1 ? '' : 's'} affected`
          : '';
      notify({
        type: 'success',
        message: `Restored to v${response.restored_from}${detail}. New version v${response.new_version_number}.`,
      });
    } else {
      notify({
        type: 'error',
        title: 'Restore failed',
        message:
          target != null
            ? `Could not restore to v${target}. Try again or check the workflow status.`
            : 'Restore failed. Try again.',
      });
    }
  };

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <h1 className={styles.title}>Workflow Versions</h1>
        <p className={styles.subtitle}>
          Append-only audit log of every change to the analysis state.
          Latest at the top; restore replays the diffs back to a
          previous point.
        </p>
      </header>

      {state.loading && state.versions.length === 0 && !state.error && (
        <div className={styles.surface}>
          <div className={styles.rail}>
            <VersionsSkeleton rows={4} />
          </div>
        </div>
      )}

      {state.error && (
        <div className={styles.errorNotice}>
          <strong>Error</strong>
          <span>{state.error}</span>
          <ButtonAny
            type="button"
            variant="outline"
            size="sm"
            onClick={() => actions.refetch()}
          >
            Retry
          </ButtonAny>
        </div>
      )}

      {!state.loading && !state.error && state.versions.length === 0 && (
        <AnalysisEmpty
          icon="history"
          title="No versions yet"
          description="A new version is saved each time the analysis is completed or revised."
        />
      )}

      <RestoreConfirmDialog
        isOpen={state.pendingRestore != null}
        target={restoreTarget}
        restoring={state.restoring}
        onCancel={actions.closeRestoreConfirm}
        onConfirm={handleConfirmRestore}
      />

      {state.versions.length > 0 && (
        <div className={styles.surface}>
          <div className={styles.rail}>
            {groupVersionsByDate(state.versions).map((group) => (
              <React.Fragment key={group.isoDate}>
                <div className={styles.dateRow}>
                  <span className={styles.dateNode} aria-hidden="true" />
                  <span className={styles.dateLabel}>
                    Versions on {group.label}
                  </span>
                </div>
                {group.versions.map((v) => (
                  <VersionCard
                    key={v.version_id}
                    version={v}
                    isExpanded={state.expanded.has(v.version_number)}
                    currentVersionNumber={state.pagination.currentVersionNumber}
                    isLoadingDetail={state.diffLoadingByNumber.has(
                      v.version_number,
                    )}
                    onToggleExpand={(n) => {
                      void actions.toggleExpand(n);
                    }}
                    onRequestRestore={actions.openRestoreConfirm}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>

          {hasMore && (
            <div className={styles.loadMoreRow}>
              <ButtonAny
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void actions.loadMore()}
                loading={state.loading}
                disabled={state.loading}
              >
                {state.loading
                  ? 'Loading…'
                  : `Load older (${state.pagination.total - state.versions.length} remaining)`}
              </ButtonAny>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
